import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { TokenProvider } from 'odyssey';
import PropTypes from 'prop-types';
import { cloneElement } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { connect } from 'react-redux';
import { useRouter } from 'shared-routing';
import { GridThemeProvider } from 'styled-bootstrap-grid';
import { ThemeProvider } from 'styled-components';

import BasketNotificator from '@common/components/BasketNotificator';
import { Routes } from '@common/constants';
import { BasketHoldWarning } from '@common/components/Notifications/BasketHoldWarning';
import { MODAL_BACKDROP_Z_INDEX } from '@common/components/Modal/ModalProvider';
import Head from '@common/components/Head';
import { useDialog } from '@common/components/Notifications';
import { Button } from 'voyager';
import { SearchIcon } from '@common/icons/SearchIcon';
import { useTranslations } from '@lang';
import { buildExpiryDialog, EXPIRY_DIALOG_ID } from './buildExpiryDialog';
import { useKeyboardInset } from '@common/hooks/useKeyboardInset';
import { GlobalStyle } from '@root/styles/global';
import { clearBasket, deleteBasketNotification, fetchBasketDetails } from '@store/basket/actions';
import { basketSelectors } from '@store/basket/selectors';
import { entitySelectors } from '@store/entity/selectors';
import { guestSelectors } from '@store/guest/selectors';
import { yourJourneySelectors } from '@store/yourJourney/selectors';

import { getPropertyTheme } from '@common/theme';
import { greyColors, semanticColors } from '@common/theme/colours';
import { gridTheme } from '@utils/grid';

import 'react-datepicker/dist/react-datepicker.css';
import 'react-phone-input-2/lib/style.css';
import 'slick-carousel/slick/slick.css';

const warningNotify = (msg) =>
  toast(
    (t) => (
      <div onClick={() => toast.dismiss(t.id)} style={{ cursor: 'pointer' }}>
        <strong>{msg.title}</strong>
        <div style={{ marginTop: 4 }}>{msg.message}</div>
      </div>
    ),
    {
      id: `${msg.title}|${msg.message}`,
      duration: 6000,
      icon: '\u26A0\uFE0F',
    }
  );

const Layout = ({
  children,
  property = {},
  language,
  basketCount,
  termsAndConditionPage = null,
  privacyAndCookiePolicyPage = null,
  cancellationPage = null,
  guest = null,
  journeyData = {},
  basketDetails = null,
  clearBasket,
  deleteBasketNotification,
  fetchBasketDetails,
  userHasAccessToSpaClasses,
  userHasAccessToFacilities,
  isCheckout = false,
}) => {
  const router = useRouter();
  const isV2 = useFeatureIsOn('feature.ecom2');
  const { show: showDialog, dismiss: dismissDialog } = useDialog();
  const { t } = useTranslations();
  useKeyboardInset();

  const dismissExpiry = () => dismissDialog(EXPIRY_DIALOG_ID);

  const searchAgain = (
    <Button
      variant="outline"
      size="small"
      onClick={() => {
        dismissExpiry();
        router.push({ pathname: Routes.HOME });
      }}
    >
      {t('common.search')}
      <SearchIcon size={24} />
    </Button>
  );

  const notifyExpiry = (msg) =>
    isV2 ? showDialog(...buildExpiryDialog(t, searchAgain, dismissExpiry)) : warningNotify(msg);

  return (
    <TokenProvider>
      <div className="page-wrapper">
        <Head property={property} />
        <ThemeProvider theme={getPropertyTheme(property)}>
          <GridThemeProvider gridTheme={gridTheme}>
            <>
              <GlobalStyle />
              <BasketNotificator
                property={property}
                propertyId={property?.id}
                basketCount={basketCount}
                basketDetails={basketDetails}
                clearBasket={clearBasket}
                deleteBasketNotification={deleteBasketNotification}
                fetchBasketDetails={fetchBasketDetails}
                language={language}
                onExpiredItemRemove={notifyExpiry}
                router={router}
              />
              <BasketHoldWarning ownsCheckout={isCheckout || router.pathname === Routes.BASKET} />
              {cloneElement(children, {
                property,
                language,
                basketCount,
                termsAndConditionPage,
                privacyAndCookiePolicyPage,
                cancellationPage,
                guest,
                journeyData,
                userHasAccessToSpaClasses,
                userHasAccessToFacilities,
              })}
              <Toaster
                position="top-right"
                containerStyle={isV2 ? { zIndex: MODAL_BACKDROP_Z_INDEX + 1 } : undefined}
                toastOptions={{
                  duration: 6000,
                  style: {
                    borderRadius: 0,
                    boxShadow: `0 3px 6px ${greyColors['grey-30']}`,
                  },
                  error: {
                    style: {
                      backgroundColor: semanticColors.redLight,
                      borderTop: `2px solid ${semanticColors.redDark}`,
                    },
                  },
                  success: {
                    style: {
                      backgroundColor: semanticColors.greenLight,
                      borderTop: `2px solid ${semanticColors.greenDark}`,
                    },
                  },
                }}
              />
            </>
          </GridThemeProvider>
        </ThemeProvider>
      </div>
    </TokenProvider>
  );
};

Layout.propTypes = {
  children: PropTypes.element.isRequired,
  property: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    defaultLanguage: PropTypes.string,
    logo: PropTypes.object,
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
    websiteUrl: PropTypes.string,
    headerFont: PropTypes.string,
    bodyFont: PropTypes.string,
    isRoomsIntegrated: PropTypes.bool,
    isTablesIntegrated: PropTypes.bool,
    isSpaIntegrated: PropTypes.bool,
    additionalLanguages: PropTypes.arrayOf(PropTypes.string),
  }),
  language: PropTypes.string.isRequired,
  basketCount: PropTypes.number.isRequired,
  termsAndConditionPage: PropTypes.object,
  privacyAndCookiePolicyPage: PropTypes.object,
  cancellationPage: PropTypes.object,
  guest: PropTypes.object,
  journeyData: PropTypes.shape({
    fromDate: PropTypes.object,
    toDate: PropTypes.object,
    adults: PropTypes.number,
    children: PropTypes.number,
  }),
  basketDetails: PropTypes.object,
  isCheckout: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  property: entitySelectors.getProperty(state),
  termsAndConditionPage: entitySelectors.getTermsAndConditionPage(state),
  privacyAndCookiePolicyPage: entitySelectors.getPrivacyAndCookiePolicyPage(state),
  cancellationPage: entitySelectors.getCancellationPage(state),
  basketCount: basketSelectors.getBasketItemCount(state),
  basketDetails: basketSelectors.getBasketDetails(state),
  guest: guestSelectors.getGuest(state),
  journeyData: yourJourneySelectors.getParsedJourneyParams(state),
  userHasAccessToSpaClasses: guestSelectors.userHasAccessToSpaEntity(state, 'classes'),
  userHasAccessToFacilities: guestSelectors.userHasAccessToSpaEntity(state, 'facilities'),
});

const mapDispatchToProps = {
  clearBasket,
  deleteBasketNotification,
  fetchBasketDetails,
};

export default connect(mapStateToProps, mapDispatchToProps)(Layout);
